import { useEffect, useState } from "react";
import {
  Box,
  Divider,
  Flex,
  Grid,
  GridProps,
  IconButton,
  MenuButton,
  MenuItem,
  MenuList,
  useClipboard,
} from "@chakra-ui/react";
import noop from "lodash/noop";
import { useTranslation } from "next-i18next";
import {
  DeleteIcon,
  Link,
  MenuHorizontalIcon,
  NextLink,
  ShareIcon,
  WatchlistFavoritesIcon,
  UsernameDisplay,
} from "@looksrare/uikit";
import { isAddressEqual, useCurrentChainInfo, useSignInHandler } from "@looksrare/utils";
import { Watchlist } from "types/graphql";
import { Popover, Text, TooltipText } from "uikit";
import { Menu } from "uikit/Menu";
import { WatchlistSelectorPopover } from "views/watchlists/components/WatchlistSelectorPopover";
import { WatchlistName } from "views/watchlists/components/WatchlistName";
import { WatchWatchlistButton } from "components/Buttons/WatchWatchlistButton";
import { AddCollectionToWatchlistButton } from "components/Buttons/AddCollectionToWatchlistButton";
import { useClearWatchlist } from "hooks/watchlists";
import { useToast } from "hooks/useToast";

interface WatchlistHeaderProps extends GridProps {
  account?: string;
  name?: string;
  owner?: Watchlist["owner"];
  watchersCount: number;
  collectionCount: number;
  isUserWatching?: boolean;
  watchlistId: number;
  description?: string;
  watchlists?: Watchlist[];
  setSelectedWatchlist?: (w: Watchlist) => void;
}

export const WatchlistHeader = ({
  account,
  name,
  owner,
  watchersCount,
  collectionCount,
  description,
  isUserWatching = false,
  watchlistId,
  watchlists,
  setSelectedWatchlist = noop,
  ...props
}: WatchlistHeaderProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const currentChainInfo = useCurrentChainInfo();

  const { onCopy, hasCopied, setValue } = useClipboard("");
  useEffect(() => {
    setValue(`${currentChainInfo.appUrl}/watchlists/${watchlistId}`);
  }, [setValue, watchlistId, currentChainInfo]);

  const [tooltipIsOpen, setTooltipIsOpen] = useState(false);
  useEffect(() => {
    let timer: NodeJS.Timeout;

    if (hasCopied) {
      setTooltipIsOpen(true);
      timer = setTimeout(() => {
        setTooltipIsOpen(false);
      }, 2000);
    }

    return () => {
      if (timer) {
        setTooltipIsOpen(false);
        clearTimeout(timer);
      }
    };
  }, [hasCopied, setTooltipIsOpen]);

  const isUsersWatchlist = isAddressEqual(account, owner?.address);

  const { mutate: clearWatchlist } = useClearWatchlist({
    onSuccess: () => {
      toast({
        title: t("Collections Removed"),
      });
    },
  });
  const { signInHandler } = useSignInHandler({ onAuthSuccess: clearWatchlist });

  const shouldHideShareButton = isUsersWatchlist && collectionCount === 0;
  const shouldHideActionButton = !isUsersWatchlist || collectionCount === 0;

  const gridTemplateAreas = {
    base: "'image actions' 'name-details name-details' 'description description'",
    md: "'image name-details actions' 'description description .'",
  };
  const gridTemplateColumns = {
    base: "64px auto",
    md: "64px auto auto",
  };

  return (
    <Grid
      rowGap={2}
      width="100%"
      gridTemplateAreas={gridTemplateAreas}
      gridTemplateColumns={gridTemplateColumns}
      columnGap={4}
      {...props}
    >
      <Flex alignItems="center" gridArea="image">
        <WatchlistFavoritesIcon boxSize={16} />
      </Flex>

      <Flex gridArea="name-details" direction="column">
        <Flex alignItems="center">
          <WatchlistName name={name} owner={owner} textStyle="display-03" color="link-02" />
          {(watchlists || []).length > 1 && (
            <WatchlistSelectorPopover watchlists={watchlists!} setSelectedWatchlist={setSelectedWatchlist} ml={2} />
          )}
        </Flex>
        <Flex gap={4}>
          {isUsersWatchlist && (
            <Text textStyle="detail" bold color="text-02">
              {t("You")}
            </Text>
          )}
          {!isUsersWatchlist && owner && (
            <Link as={NextLink} href={`/accounts/${owner.address}`}>
              <UsernameDisplay address={owner.address} color="text-02" textStyle="detail" bold />
            </Link>
          )}
          {(isUsersWatchlist || owner) && <Divider orientation="vertical" color="border-01" />}
          <Text textStyle="detail" bold color="text-03">
            {watchersCount === 1
              ? t("{{watchersCount}} watcher", { watchersCount })
              : t("{{watchersCount}} watchers", { watchersCount })}
          </Text>
          <Text textStyle="detail" bold color="text-03">
            {collectionCount === 1
              ? t("{{collectionCount}} collection", { collectionCount })
              : t("{{collectionCount}} collections", { collectionCount })}
          </Text>
        </Flex>
      </Flex>
      <Flex gridArea="actions" gap={3} justifyContent="flex-end">
        {isUsersWatchlist ? (
          <AddCollectionToWatchlistButton />
        ) : (
          <WatchWatchlistButton watchlistId={watchlistId} isUserWatching={isUserWatching} />
        )}
        {!shouldHideShareButton && (
          <Popover isOpen={tooltipIsOpen} placement="top" label={<TooltipText>{t("Copied!")}</TooltipText>}>
            <IconButton
              aria-label="watchlist-share-button"
              size="sm"
              variant="outline"
              colorScheme="secondary"
              onClick={onCopy}
              data-id="watchlist-share-button"
            >
              <ShareIcon />
            </IconButton>
          </Popover>
        )}
        {!shouldHideActionButton && (
          <Box>
            <Menu>
              <MenuButton as={IconButton} aria-label="share" variant="outline" colorScheme="gray" size="sm">
                <MenuHorizontalIcon />
              </MenuButton>
              <MenuList zIndex="dropdown">
                <MenuItem icon={<DeleteIcon />} onClick={() => signInHandler()} data-id="watchlist-clear">
                  {t("Clear Watchlist")}
                </MenuItem>
              </MenuList>
            </Menu>
          </Box>
        )}
      </Flex>

      {!!description && (
        <Text color="text-03" gridArea="description">
          {description}
        </Text>
      )}
    </Grid>
  );
};
